import {
  Collection,
  EntityListFilterByOperator,
  Space,
  SpaceType,
} from '@tribeplatform/gql-client/types'
import { useI18n } from '@tribeplatform/react-components/i18n'
import {
  useAuthMember,
  useCollections,
  useSpaces,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'

import { useBlock } from '../../hooks/block.hook.js'

import {
  CollectionMenuEntry,
  CollectionMenuItem,
  CollectionMenuSpaceFilter,
  CollectionMenuViewStyle,
  NO_COLLECTION_ID,
} from './types.js'
import { buildNoCollectionEntry } from './utils.js'

// Structural shape — matches the web-side SSR loader by field names only,
// to keep this package free of a web dependency.
type CollectionMenuSsrOutput = {
  collections: Collection[]
  prefetchedAt: number
}
const readSsrOutput = (
  output: unknown,
): CollectionMenuSsrOutput | undefined => {
  if (!output || typeof output !== 'object') {
    return undefined
  }
  const o = output as Partial<CollectionMenuSsrOutput>
  return Array.isArray(o.collections) && typeof o.prefetchedAt === 'number'
    ? (o as CollectionMenuSsrOutput)
    : undefined
}

const SPACES_LIMIT = 100

export const useCollectionMenuEntries = ({
  items,
  viewStyle,
  spaceFilter,
}: {
  items?: CollectionMenuItem[]
  viewStyle: CollectionMenuViewStyle
  spaceFilter: CollectionMenuSpaceFilter
}): {
  entries: CollectionMenuEntry[]
  isLoading: boolean
  isReady: boolean
} => {
  const { $t } = useI18n()
  const { data: member, isGuest } = useAuthMember()
  const { block } = useBlock()
  // Only consume the seed for the unpersonalized All filter — must match
  // the loader's gate (collection-menu.loader.ts).
  const ssrInitial =
    spaceFilter === CollectionMenuSpaceFilter.All
      ? readSsrOutput(block?.output)
      : undefined

  const noCollectionEnabled = !!items?.find(
    item => item.id === NO_COLLECTION_ID,
  )?.enabled

  const spaceFields =
    viewStyle === CollectionMenuViewStyle.Simple
      ? // Needed to collapse locale groups to their primary space in the nav.
        { localeGroup: 'basic' as const }
      : {
          image: 'basic' as const,
          banner: 'basic' as const,
          slate: 'all' as const,
          authMemberProps: {},
          // Needed to collapse locale groups to their primary space in the nav.
          localeGroup: 'basic' as const,
        }

  const exploreOnly =
    !isGuest && spaceFilter === CollectionMenuSpaceFilter.NotJoined
  const memberId =
    !isGuest && spaceFilter === CollectionMenuSpaceFilter.Joined
      ? member?.id
      : null

  const { data: collections, isInitialLoading: isCollectionsLoading } =
    useCollections({
      fields: {
        spaces: {
          fields: spaceFields,
          variables: { limit: SPACES_LIMIT, exploreOnly, memberId },
        },
      },
      variables: {},
      useQueryOptions: {
        retry: 10,
        retryDelay: 1000,
        // Match the Posts/Feed prefetch contract — suppress the immediate
        // background refetch react-query would otherwise fire on mount.
        staleTime: 30_000,
        ...(ssrInitial
          ? {
              initialData: ssrInitial.collections,
              initialDataUpdatedAt: ssrInitial.prefetchedAt,
            }
          : {}),
      },
    })

  const { data: orphanSpacesData, isInitialLoading: isOrphanSpacesLoading } =
    useSpaces({
      variables: {
        limit: SPACES_LIMIT,
        type: [SpaceType.Group, SpaceType.Broadcast],
        filterBy: [
          {
            key: 'groupId',
            operator: EntityListFilterByOperator.hasValue,
            value: 'false',
          },
        ],
        exploreOnly,
        memberId,
      },
      fields: spaceFields,
      useInfiniteQueryOptions: { enabled: noCollectionEnabled },
    })

  const isReady = Array.isArray(collections)
  const isLoading =
    isCollectionsLoading || (noCollectionEnabled && isOrphanSpacesLoading)

  if (!isReady) {
    return { entries: [], isLoading, isReady }
  }

  if (!noCollectionEnabled) {
    return { entries: collections, isLoading, isReady }
  }

  const orphanSpaces = simplifyPaginatedResult<Space>(orphanSpacesData)
  const noCollectionName = $t({
    id: 'Generics.NoCollection',
    defaultMessage: 'No {COLLECTION}',
  }) as string

  const entries: CollectionMenuEntry[] = [
    ...collections,
    buildNoCollectionEntry(noCollectionName, {
      nodes: orphanSpaces.nodes,
      totalCount: orphanSpaces.totalCount,
    }),
  ]

  return { entries, isLoading, isReady }
}
